import chatMock from '@src/services/chatMock';
import { axiosInstance, requestAPI } from '@src/utils/fetch';

/**
 * 쪽지(비실시간) 채팅 API. pple-api의 /api/v1/chat/* 를 호출한다.
 * 소켓이 아니라 프론트가 getMessages(afterId)를 폴링해서 새 메시지를 가져온다.
 */

export interface ChatRoomSummary {
  uuid: string;
  opponentUuid: string;
  opponentName: string;
  opponentImage: string | null;
  donationUuid: string | null;
  donationTitle: string | null;
  /** 상대 역할 태그: "수혈 요청" | "헌혈자" (기존 방은 null) */
  opponentRole: string | null;
  /** 사연 혈액형(예: "O형 Rh+") */
  donationBloodType: string | null;
  lastMessageContent: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
}

export interface ChatRoomDetail {
  uuid: string;
  opponentUuid: string;
  opponentName: string;
  opponentImage: string | null;
  donationUuid: string | null;
  donationTitle: string | null;
  opponentRole: string | null;
  donationBloodType: string | null;
  donationRegion: string | null;
  createdAt: string | null;
  opponentLastReadAt: string | null; // 상대가 마지막으로 읽은 시각 → 내 메시지 "읽음" 계산
}

export interface ChatMessage {
  id: number;
  uuid: string;
  roomUuid: string;
  senderUuid: string;
  content: string;
  fileUrl: string | null;
  type: 'TEXT' | 'IMAGE' | 'SYSTEM';
  createdAt: string;
  parentMessageId: number | null;
  parentSenderUuid: string | null;
  parentContent: string | null;
}

const CHAT = '/api/v1/chat';

const createOrGetRoom = async (
  donationUuid: string
): Promise<ChatRoomDetail> => {
  const { data } = await requestAPI().post(`${CHAT}/rooms`, { donationUuid });
  return data;
};

const getRooms = async (): Promise<ChatRoomSummary[]> => {
  const { data } = await requestAPI().get(`${CHAT}/rooms`);
  return data;
};

const getRoom = async (roomUuid: string): Promise<ChatRoomDetail> => {
  const { data } = await requestAPI().get(
    `${CHAT}/rooms/${encodeURIComponent(roomUuid)}`
  );
  return data;
};

const getMessages = async (
  roomUuid: string,
  opts?: { afterId?: number; beforeId?: number; size?: number }
): Promise<ChatMessage[]> => {
  const params = new URLSearchParams();
  if (opts?.afterId != null) params.set('afterId', String(opts.afterId));
  if (opts?.beforeId != null) params.set('beforeId', String(opts.beforeId));
  if (opts?.size != null) params.set('size', String(opts.size));
  const qs = params.toString();
  const { data } = await requestAPI().get(
    `${CHAT}/rooms/${encodeURIComponent(roomUuid)}/messages${
      qs ? `?${qs}` : ''
    }`
  );
  return data;
};

const sendMessage = async (
  roomUuid: string,
  content: string,
  parentMessageId?: number | null
): Promise<ChatMessage> => {
  const { data } = await requestAPI().post(
    `${CHAT}/rooms/${encodeURIComponent(roomUuid)}/messages`,
    { content, parentMessageId: parentMessageId ?? null }
  );
  return data;
};

const sendImage = async (
  roomUuid: string,
  file: File
): Promise<ChatMessage> => {
  const form = new FormData();
  form.append('image', file);
  // 멀티파트는 axiosInstance로 직접 — 브라우저가 boundary 포함 Content-Type 설정
  const { data } = await axiosInstance.post(
    `${CHAT}/rooms/${encodeURIComponent(roomUuid)}/image`,
    form
  );
  return data;
};

const reportRoom = async (
  roomUuid: string,
  content?: string
): Promise<void> => {
  await requestAPI().post(
    `${CHAT}/rooms/${encodeURIComponent(roomUuid)}/report`,
    { content: content ?? '' }
  );
};

const blockOpponent = async (roomUuid: string): Promise<void> => {
  await requestAPI().post(
    `${CHAT}/rooms/${encodeURIComponent(roomUuid)}/block`
  );
};

const markRead = async (roomUuid: string): Promise<void> => {
  await requestAPI().post(`${CHAT}/rooms/${encodeURIComponent(roomUuid)}/read`);
};

const getUnreadCount = async (): Promise<number> => {
  const { data } = await requestAPI().get(`${CHAT}/unread-count`);
  return data?.unreadCount ?? 0;
};

const realApi = {
  createOrGetRoom,
  getRooms,
  getRoom,
  getMessages,
  sendMessage,
  sendImage,
  reportRoom,
  blockOpponent,
  markRead,
  getUnreadCount,
};

/**
 * 로컬 채팅 테스트 모드 (백엔드 없이 더미데이터). .env.local의 NEXT_PUBLIC_CHAT_MOCK=true
 * 운영 빌드(NODE_ENV=production)에서는 플래그가 켜져 있어도 절대 동작하지 않는다.
 */
export const isChatMock = () =>
  process.env.NODE_ENV !== 'production' &&
  process.env.NEXT_PUBLIC_CHAT_MOCK === 'true';

// mock 모드면 chatMock으로, 아니면 실제 API로 위임한다.
const chatApi: typeof realApi = new Proxy(realApi, {
  get(target, prop: string) {
    if (isChatMock() && prop in chatMock) {
      return (chatMock as any)[prop];
    }
    return (target as any)[prop];
  },
});

export default chatApi;
